import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiArrowLeft, FiArrowRight, FiCheck, FiMail } from 'react-icons/fi';
import AuthLayout from './AuthLayout';
import AuthField from './AuthField';

const STEPS = ['Username', 'Email', 'Code', 'Password'];
const RESEND_SECONDS = 30;

// "jo****@mail.com" — enough to recognise the address without revealing it.
const maskEmail = email => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (local.length <= 2) return `${local}***@${domain}`;
    return `${local.slice(0, 2)}${'*'.repeat(local.length - 2)}@${domain}`;
};

/**
 * Four-step account recovery shared by drivers and customers:
 * username → pick an email on file → 6-digit code → new password.
 *
 * apiBase:   e.g. `${API}/driver/password/reset`
 * idKey:     the id field the API expects back ("driverId" / "partnerId")
 */
export default function PasswordResetPage({ bannerRole, brand, apiBase, idKey, signinPath, help }) {
    const [step, setStep] = useState(1);
    const [username, setUsername] = useState('');
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState('');
    const [accountId, setAccountId] = useState(null);
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [resendIn, setResendIn] = useState(0);
    const otpRef = useRef(null);

    useEffect(() => {
        if (resendIn <= 0) return undefined;
        const t = setTimeout(() => setResendIn(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendIn]);

    useEffect(() => {
        if (step === 3) otpRef.current?.focus();
    }, [step]);

    // Resolves to the response body on success, or null after showing the error.
    const post = async (path, body) => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`${apiBase}/${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await response.json();
            if (data.error) {
                setError(data.error);
                return null;
            }
            return data;
        } catch {
            setError("We couldn't reach the server. Check your connection and try again.");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const verifyUsername = async () => {
        if (!username.trim()) return setError('Enter your username.');
        const data = await post('username/verify', { username: username.trim() });
        if (!data) return;
        const found = data.emails || [data.email];
        setEmails(found);
        setSelectedEmail(found.length === 1 ? found[0] : '');
        setAccountId(data.id);
        setStep(2);
    };

    const sendCode = async () => {
        if (!selectedEmail) return setError('Choose where to send the code.');
        const data = await post('otp/send', { [idKey]: accountId, email: selectedEmail });
        if (!data) return;
        setResendIn(RESEND_SECONDS);
        setStep(3);
    };

    const verifyCode = async (code = otp) => {
        if (code.length !== 6) return setError('Enter the 6-digit code from the email.');
        const data = await post('otp/verify', { [idKey]: accountId, otp: code });
        if (data) setStep(4);
        else otpRef.current?.select(); // typing the corrected code replaces the old one
    };

    const resetPassword = async () => {
        if (newPassword.length < 6) return setError('Password must be at least 6 characters.');
        if (newPassword !== confirmPassword) return setError("Passwords don't match.");
        const data = await post('after/otp/verification', { [idKey]: accountId, newPassword });
        if (data) setStep(5);
    };

    const onOtpChange = e => {
        const code = e.target.value.replace(/\D/g, '').slice(0, 6);
        setOtp(code);
        setError('');
        // Pasted or autofilled codes go straight through.
        if (code.length === 6 && !loading) verifyCode(code);
    };

    const goBack = () => {
        setError('');
        if (step === 2) { setEmails([]); setSelectedEmail(''); }
        if (step === 3) setOtp('');
        if (step === 4) { setNewPassword(''); setConfirmPassword(''); }
        setStep(s => s - 1);
    };

    const submit = e => {
        e.preventDefault();
        if (loading) return;
        if (step === 1) verifyUsername();
        else if (step === 2) sendCode();
        else if (step === 3) verifyCode();
        else if (step === 4) resetPassword();
    };

    const primaryLabel = { 1: 'Continue', 2: 'Send code', 3: 'Verify code', 4: 'Reset password' }[step];

    if (step === 5) {
        return (
            <AuthLayout bannerRole={bannerRole} brand={brand} back={null}>
                <div className="ta-done">
                    <span className="ta-done-icon"><FiCheck size={30} aria-hidden="true" /></span>
                    <h2 className="ta-title">Password updated</h2>
                    <p className="ta-sub">You can sign in with your new password now.</p>
                    <Link to={signinPath} className="ta-btn">
                        Go to sign in <FiArrowRight size={18} aria-hidden="true" />
                    </Link>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout bannerRole={bannerRole} brand={brand} back={{ to: signinPath, label: 'Sign in' }}>
            <div className="ta-progress" aria-label={`Step ${step} of ${STEPS.length}: ${STEPS[step - 1]}`}>
                {STEPS.map((label, i) => (
                    <span key={label} className={`ta-progress-seg${i < step ? ' is-done' : ''}`} />
                ))}
            </div>
            <p className="ta-step-label">Step {step} of {STEPS.length}</p>

            {step === 1 && <>
                <h2 className="ta-title">Reset your password</h2>
                <p className="ta-sub">Enter your username and we'll email you a code to set a new password.</p>
            </>}
            {step === 2 && <>
                <h2 className="ta-title">Where should we send the code?</h2>
                <p className="ta-sub">Pick one of the email addresses on your account.</p>
            </>}
            {step === 3 && <>
                <h2 className="ta-title">Check your email</h2>
                <p className="ta-sub">We sent a 6-digit code to <strong>{maskEmail(selectedEmail)}</strong>.</p>
            </>}
            {step === 4 && <>
                <h2 className="ta-title">Choose a new password</h2>
                <p className="ta-sub">At least 6 characters. Pick one you don't use anywhere else.</p>
            </>}

            <form onSubmit={submit} noValidate>
                <div className="ta-fields">
                    {step === 1 && (
                        <AuthField
                            id="reset-username"
                            label="Username"
                            value={username}
                            onChange={e => { setUsername(e.target.value); setError(''); }}
                            autoComplete="username"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            autoFocus
                        />
                    )}

                    {step === 2 && (
                        <div className="ta-options" role="radiogroup" aria-label="Email address">
                            {emails.map((email, i) => (
                                <label key={`${email}-${i}`} className={`ta-option${selectedEmail === email ? ' is-selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="reset-email"
                                        value={email}
                                        checked={selectedEmail === email}
                                        onChange={() => { setSelectedEmail(email); setError(''); }}
                                    />
                                    <FiMail size={18} aria-hidden="true" />
                                    <span>{maskEmail(email)}</span>
                                </label>
                            ))}
                        </div>
                    )}

                    {step === 3 && (
                        <input
                            ref={otpRef}
                            className="ta-input ta-otp"
                            value={otp}
                            onChange={onOtpChange}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]*"
                            maxLength={6}
                            placeholder="••••••"
                            aria-label="6-digit code"
                        />
                    )}

                    {step === 4 && <>
                        <AuthField
                            id="reset-new"
                            label="New password"
                            type="password"
                            value={newPassword}
                            onChange={e => { setNewPassword(e.target.value); setError(''); }}
                            autoComplete="new-password"
                            autoFocus
                        />
                        <AuthField
                            id="reset-confirm"
                            label="Confirm new password"
                            type="password"
                            value={confirmPassword}
                            onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                            autoComplete="new-password"
                        />
                    </>}
                </div>

                {step === 3 && (
                    <div className="ta-row">
                        <span className="ta-muted">Didn't get it? Check spam.</span>
                        <button
                            type="button"
                            className="ta-link ta-link-btn"
                            onClick={sendCode}
                            disabled={loading || resendIn > 0}
                        >
                            {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                        </button>
                    </div>
                )}

                {error && (
                    <div className="ta-alert" role="alert">
                        <FiAlertCircle size={18} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                <button type="submit" className="ta-btn" disabled={loading}>
                    {loading
                        ? <><span className="ta-spinner" aria-hidden="true" /> Please wait…</>
                        : <>{primaryLabel} <FiArrowRight size={18} aria-hidden="true" /></>}
                </button>

                {step > 1 && (
                    <button type="button" className="ta-btn-quiet" onClick={goBack} disabled={loading}>
                        <FiArrowLeft size={16} aria-hidden="true" /> Back
                    </button>
                )}
            </form>

            {help && <p className="ta-help">{help}</p>}
        </AuthLayout>
    );
}
